import { Injectable } from "@angular/core";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  UrlTree,
} from "@angular/router";
import { Store } from "@ngxs/store";
import { Observable, of } from "rxjs";
import { UserState } from "../../store/user/user.store";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { LoginByToken } from "src/app/store/user/user.actions";
import { AuthContract } from "src/app/shared/contracts/auth.contract";

@Injectable({ providedIn: "root" })
export class EventRegistrationGuard implements CanActivate {
  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private authContract: AuthContract
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<true | UrlTree> {
    const tokenFromQuery = route.queryParams["token"];
    const landingUrl = `/events/event/${route.params["eventId"]}`;

    if (tokenFromQuery) {
      return this.authContract.getAuthByToken(tokenFromQuery).pipe(
        switchMap((authUser) => {
          if (!authUser) {
            return of(this.router.parseUrl(landingUrl));
          }

          this.store.dispatch(new LoginByToken(tokenFromQuery));

          return this.store.select(UserState.authToken).pipe(
            filter(
              (authToken) => authToken !== undefined && authToken !== null
            ), // wait until we get a result
            take(1),
            map((authToken) => {
              if (authToken && authToken.token) {
                const reconstructedUrl =
                  "/" +
                  route.routeConfig?.path
                    ?.split("/")
                    .map((segment) => {
                      if (segment.startsWith(":")) {
                        const paramName = segment.slice(1);
                        return route.params[paramName];
                      }
                      return segment;
                    })
                    .join("/");
                // remove token from url
                this.router.navigateByUrl(reconstructedUrl, {
                  replaceUrl: true,
                });
                return true;
              } else {
                console.log("Failed to log in with token, redirecting");
                return this.router.parseUrl(landingUrl);
              }
            })
          );
        }),
        catchError((error) => {
          console.error("Auth token failed", error);
          return of(this.router.parseUrl(landingUrl));
        })
      );
    }

    // No token fallback
    return this.store
      .select(UserState.authToken)
      .pipe(
        map((authToken) =>
          authToken?.token ? true : this.router.parseUrl(landingUrl)
        )
      );
  }
}
